import { Navigation } from '../../enums/navigation-menus';
import { MediaType } from '@nice-ccf/core-sdk';
import {
  CcfAssignementBoxIcon,
  CcfCalendarIcon,
  CcfConversationIcon,
  CcfCustomerCardIcon,
  CcfDashboardIcon,
  CcfDirectoryIcon,
  CcfFlashIcon,
  CcfHelpIcon,
  CcfHomeIcon,
  CcfInteractionSearchIcon,
  CcfQueueIcon,
  CcfReportingIcon,
  CcfSettingsIcon,
  CcfWemIcon,
  CcfWorkHistoryIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  Grid,
  TooltipProps,
  useTheme,
  Theme,
  useMediaQuery,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { useDispatch, useSelector } from 'react-redux';

import CcfNavigationItemsStyles from './ccf-navigation-items-styles';
import CcfCustomerCard from '../ccf-inbox/ccf-customer-card/ccf-customer-card';
import { activeContactSelector } from '../ccf-inbox/ccf-inbox.slice';
import {
  CcfAppAgentMessages,
  CcfAppEnlighten,
  CcfAppSchedule,
  CcfAppSettings,
  CcfAppWem,
  CcfContactHistory,
  CcfCustomWorkspacePopover,
  CcfDirectory,
  CcfInteractionSearch,
  CcfInteractionWorkspace,
  CcfQuickReplies,
  CcfReporting,
  globalActions,
} from '@nice-ccf/ui-components';
import { CcfQueueCounter } from '../ccf-queue-counter/ccf-queue-counter';
import CcfCustomWorkspace, {
  PersistentPanelFrame,
} from '../ccf-custom-workspace/ccf-custom-workspace';

export interface NavigationItemLS {
  menuName: Navigation;
}

export interface NavigationItem {
  menuName: Navigation;
  icon?: React.ReactElement;
  tooltip: string;
  toolTipPlacement?: TooltipProps['placement'];
  className?: string;
  contactApp?: boolean;
  onClick?: (
    e?: React.MouseEvent<HTMLElement, MouseEvent>,
    ...rest: any
  ) => void;
  children?: React.ReactNode;
  /**
   * showBadge: if true, Badge Component will be shown beside the menuitem
   */
  showBadge?: boolean;
  /**
   * appSpaceComponent: React Component, to be shown in App Space
   */
  appSpaceComponent?: React.ReactElement;
  /**
   * workSpaceComponent: React Component, to be shown in workspace
   */
  workSpaceComponent?: React.ReactElement | any;
  /**
   * allowMessaging: communicates with external apps in iframe through postMessage if true
   */
  allowMessaging?: boolean;
  /**
   * externalUrl: Url to load in iframe for external apps
   */
  externalUrl?: string;
  /**
   * productId: show this item only when the product is enabled in TM product catalog (e.g. WEM)
   */
  productId?: string;
  /*
   * If true, the navigation item will be displayed in the list
   */
  isActive?: boolean;
  /*
   * If true, then appspace will come at the right sidebar
   */
  showAppSpace?: boolean;
  /**
   * helpUrl: Link to help document
   */
  helpUrl?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rightGridContainer: {
      display: 'flex',
      height: '98%',
      flex: '1',
      overflowY: 'hidden',
      [theme.breakpoints.up('xl')]: {
        padding: '8px',
      },
      [theme.breakpoints.up('sm')]: {
        height: '100%',
      },
    },
    heightFull: {
      height: '100%',
      [theme.breakpoints.only('xs')]: {
        display: 'none',
      },
      [theme.breakpoints.only('xl')]: {
        display: 'flex',
      },
    },
    fullView: {
      height: '100%',
      [theme.breakpoints.down('xl')]: {
        width: '100%',
        padding: 0,
      },
    },
    rightContainer: {
      display: 'flex',
      height: '98%',
      flex: '1',
      [theme.breakpoints.down('xl')]: {
        minWidth: '17vw !important',
      },
      [theme.breakpoints.up('xl')]: {
        padding: '8px',
      },
    },
    rightIntContainer: {
      display: 'flex',
      height: '86%',
      flex: '1',
      width: '100% !important',
    },
    interactionSearchIconMobileView: {
      [theme.breakpoints.only('sm')]: {
        marginTop: '10px',
      },
    },
    dashboardIconMobileView: {
      [theme.breakpoints.only('sm')]: {
        marginTop: '10px',
      },
    },
  })
);

/**
 * This component is used to fetch the list of available navigation items for AppSpace and Workspace
 * @example - NavigationItems()
 */
export function useNavigationItems(
  isAppSpace?: boolean,
  isBelowLg?: boolean
): NavigationItem[] {
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const theme = useTheme();
  const styles = CcfNavigationItemsStyles(theme);
  const htmlColor = theme.palette.secondary.main;
  const activeContact = useSelector(activeContactSelector);
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const classes = useStyles();
  const toolTipPlacement = isBelowLg ? 'bottom' : 'right';
  const { setSelectedMenu } = globalActions;
  const isCallOnIntegrated =
    activeContact?.media === 'Voice' &&
    activeContact.contactStatus === 'active' &&
    isSmView;

  /**
   * Used to render the icons based on selected Menu
   * @param menuName - string
   * @example - iconRenderer
   */
  const iconRenderer = (menuName: string, htmlColor: string) => {
    switch (menuName) {
      case 'contactHistory':
        return (
          <CcfWorkHistoryIcon htmlColor={htmlColor} viewBox="-2 -4 24 24" />
        );
      case 'interactionSearch':
        return (
          <CcfInteractionSearchIcon
            fillColor={'#2D5651'}
            className={classes.interactionSearchIconMobileView}
          />
        );
      case 'assignment':
        return (
          <CcfAssignementBoxIcon htmlColor={htmlColor} viewBox="-2 -4 24 24" />
        );
      case 'customerCard':
        return (
          <CcfCustomerCardIcon
            htmlColor={htmlColor}
            viewBox="0 -4 32 22"
            style={{ fill: '#444d57', stroke: '#fff' }}
          />
        );
      case 'schedule':
        return <CcfCalendarIcon htmlColor={htmlColor} viewBox="-2 -4 24 24" />;
      case 'directory':
        return <CcfDirectoryIcon htmlColor={htmlColor} viewBox="-2 -4 24 24" />;
      case 'queue':
        return <CcfQueueIcon htmlColor={htmlColor} viewBox="-2 -6 24 24" />;
      case 'wem':
        return <CcfWemIcon htmlColor={htmlColor} viewBox="-2 -4 24 24" />;
      case 'agentMessages':
        return <CcfConversationIcon />;
      case 'settings':
        return <CcfSettingsIcon htmlColor={htmlColor} viewBox="-2 -4 24 24" />;
      // case 'launch':
      //   return <LaunchPopover
      //     htmlColor={htmlColor}
      //     viewBox="9 6 24 24"
      //     style={isSmView ? { top: '-38px', left: '-38px' } : { top: '2px', left: '40px' }}
      //     tooltipPlacement='top'
      //   />;
      case 'quickReply':
        return <CcfFlashIcon />;
      case 'reports':
        return <CcfDashboardIcon className={classes.dashboardIconMobileView} />;
      // case 'help':
      //   return <CcfHelpIcon htmlColor={htmlColor} viewBox="0 0 24 24"/>
      case 'customWorkspace':
        return <CcfCustomWorkspacePopover />;
      case '':
        return <CcfWorkHistoryIcon />;
      default:
        return undefined;
    }
  };

  const navigationArray: NavigationItem[] = [
    {
      menuName: Navigation.CUSTOMERCARD,
      icon: iconRenderer('CustomerCard', htmlColor),
      tooltip: translate('customerCard'),
      appSpaceComponent: <CcfCustomerCard />,
      isActive: false,
      showAppSpace: true,
      contactApp: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.CONTACTHISTORY,
      icon: iconRenderer('contactHistory', htmlColor),
      tooltip: translate('contactHistory'),
      workSpaceComponent: (
        <Grid className={classes.rightGridContainer}>
          <Grid
            item
            sx={{ ml: 0 }}
            xs={12}
            sm={12}
            lg={12}
            className={classes.fullView}
          >
            <CcfContactHistory isAppSpace={false} />
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      contactApp: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.INTERACTIONSEARCH,
      icon: iconRenderer('interactionSearch', htmlColor),
      tooltip: translate('interactionSearch'),
      workSpaceComponent: (
        <Grid className={classes.rightGridContainer}>
          <Grid
            item
            sx={{ ml: 0 }}
            xs={12}
            sm={12}
            lg={12}
            className={classes.fullView}
          >
            <CcfInteractionSearch />
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      contactApp: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.QUEUE,
      icon: iconRenderer('queue', htmlColor),
      tooltip: translate('queueCounter'),
      workSpaceComponent: (
        <Grid className={classes.rightGridContainer}>
          <Grid
            item
            sx={{ ml: 0 }}
            xs={12}
            sm={12}
            lg={12}
            className={classes.fullView}
          >
            <CcfQueueCounter />
          </Grid>
        </Grid>
      ),
      showBadge: true,
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.DIRECTORY,
      icon: iconRenderer('directory', htmlColor),
      tooltip: translate('directory'),
      appSpaceComponent: <CcfDirectory isFullView={false} />,
      workSpaceComponent: (
        <Grid className={classes.rightGridContainer}>
          <Grid
            item
            sx={{ ml: 0 }}
            xs={12}
            sm={12}
            lg={12}
            className={classes.fullView}
          >
            <CcfDirectory isFullView={true} />
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.CALENDER,
      icon: iconRenderer('schedule', htmlColor),
      tooltip: translate('schedule'),
      appSpaceComponent: <CcfAppSchedule isfullView={false} />,
      workSpaceComponent: (
        <Grid className={classes.rightGridContainer}>
          <Grid
            item
            sx={{ ml: 0 }}
            xs={12}
            sm={12}
            xl={12}
            className={classes.fullView}
          >
            <CcfAppSchedule isfullView={true} />
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.WEM,
      icon: iconRenderer('wem', htmlColor),
      tooltip: translate('wem'),
      workSpaceComponent: (
        <Grid sx={{ display: 'flex', height: '100%', flex: '1' }}>
          <Grid
            item
            sx={{ display: { lg: 'flex' }, ml: 0, width: '100%' }}
            padding={1}
            xl={12}
          >
            <CcfAppWem />
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.REPORTS,
      icon: iconRenderer('reports', htmlColor),
      tooltip: translate('reports'),
      workSpaceComponent: (
        <Grid
          sx={{ overflowY: 'auto', display: 'flex', height: '100%', flex: '1' }}
        >
          <Grid
            item
            sx={{ display: { lg: 'flex' }, ml: 0, width: '100%' }}
            padding={1}
            xl={12}
          >
            <CcfReporting />
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      contactApp: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.SENTIMENT,
      icon: iconRenderer('Sentiment', htmlColor),
      tooltip: translate('sentiment'),
      appSpaceComponent: <CcfAppEnlighten />,
      isActive: false,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.ENLIGHTEN,
      icon: iconRenderer('Enlighten', htmlColor),
      tooltip: translate('enlighten'),
      appSpaceComponent: <CcfAppEnlighten />,
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.QUICK_REPLY,
      icon: iconRenderer('quickReply', htmlColor),
      tooltip: translate('quickReply'),
      appSpaceComponent: <CcfAppEnlighten />,
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.AGENT_MESSAGES,
      icon: iconRenderer('agentMessages', htmlColor),
      tooltip: translate('agentMessages'),
      workSpaceComponent: (
        <Grid
          sx={{ overflowY: 'auto', display: 'flex', height: '100%', flex: '1' }}
        >
          <Grid
            item
            sx={{ display: { lg: 'flex' }, ml: 0, width: '100%' }}
            padding={1}
            xl={12}
          >
            {<CcfAppAgentMessages />}
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
    {
      menuName: Navigation.SETTINGS,
      icon: iconRenderer('settings', htmlColor),
      tooltip: translate('settings'),
      workSpaceComponent: (
        <Grid
          sx={{ overflowY: 'auto', display: 'flex', height: '100%', flex: '1' }}
        >
          <Grid
            item
            sx={{ display: { lg: 'flex' }, ml: 0, width: '100%' }}
            padding={1}
            xl={12}
          >
            {<CcfAppSettings />}
          </Grid>
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      toolTipPlacement: 'right',
    },
    {
      menuName: Navigation.CUSTOMWORKSPACE,
      icon: iconRenderer('customWorkspace', htmlColor),
      tooltip: translate('customWorkspace'),
      workSpaceComponent: (activeCustomSpaceIframe: PersistentPanelFrame) => (
        <Grid
          className={classes.rightGridContainer}
          item
          sx={{ display: { lg: 'flex' }, ml: 0 }}
          padding={1}
          sm={12}
        >
          <CcfCustomWorkspace
            activeCustomSpaceIframe={activeCustomSpaceIframe}
          />
        </Grid>
      ),
      isActive: true,
      showAppSpace: false,
      toolTipPlacement,
    },
  ];

  return isAppSpace
    ? navigationArray.filter(
        (navItem: NavigationItem) =>
          navItem.appSpaceComponent && navItem.isActive
      )
    : navigationArray.filter(
        (navItem: NavigationItem) =>
          navItem.workSpaceComponent && navItem.isActive
      );
}
